import { v4 as uuidv4 } from "uuid";
import { cmpCookie } from "@foundation/cmp-client";
import DependencyLoader from "../DependencyLoader";
import Environment from "../util/Environment";
import PerformanceMonitor from "../util/monitoring/PerformanceMonitor";
import stripSlashes from "../util/stripSlashes";
import { SLOT_ONLOAD_EVENT, SLOT_RENDER_EVENT, SLOT_REQUESTED, SLOT_RESPONSE_RECEIVED_EVENT } from "../constants";
import fireEREvt from "../util/eventRecorderClient";
import DoubleVerifyTracking from "../doubleVerify/DoubleVerifyTracking";
import Reporter from "../util/monitoring/Reporter";
import Viewability, { AdSizeMap } from "../viewability/Viewability";
import { Targeting } from "../interfaces/TargetingProvider";
import UserInfo from "../session/UserInfo";
import { WatchlistProvider } from "../watchlists/WatchlistProvider";
import AdsServiceClient from "../adsService/AdsServiceClient";
import Ad from "./Ad";

const GPT_SRC = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";

/**
 * Refresh depths at or above this are reported to GAM as a single bucket.
 * Values 0 through 9 are reported individually.
 */
const MAX_REPORTED_REFRESH = 10;

const SERVICES = {
    pubads: "pubads",
    companionAds: "companionAds"
};

export type ServiceConfig = {
    services: string[];
    display?: boolean;
    changeCorrelator?: boolean;
}

export default class GPT extends DependencyLoader {

    private _performanceMonitor = new PerformanceMonitor("Sparkle:GPT");
    private _cachedSlots: Record<string, googletag.Slot> = {};
    private _networkCode: string;
    private _renderedAds: Record<string, Ad> = {};
    private _googletag = window.googletag;
    private _tagLoadedToFirstAdMetric: Reporter | null;
    private _doubleVerifyTracking: DoubleVerifyTracking;
    private _adsServiceClient: AdsServiceClient;
    private _watchlistProvider: WatchlistProvider;
    private _enableLazyLoad: boolean;
    services = SERVICES;

    constructor(
        networkCode: string,
        doubleVerifyTracking: DoubleVerifyTracking,
        adsServiceClient: AdsServiceClient,
        watchlistProvider: WatchlistProvider,
        enableLazyLoad = false
    ) {
        super();

        this._networkCode = networkCode;
        this._tagLoadedToFirstAdMetric = this._performanceMonitor.createMetric("tagLoadedToFirstAdMetric");
        this._doubleVerifyTracking = doubleVerifyTracking;
        this._adsServiceClient = adsServiceClient;
        this._watchlistProvider = watchlistProvider;
        this._enableLazyLoad = enableLazyLoad;

        this._contextifyCallbacks();
        this._listenToSlotRenderEvents();
        this._collapseEmptyDivs();

        // This will be the very first cmd to be enqueue to GPT.
        // The purpose is to mark the time when GPT queue is ready, which
        // should represent the time when gpt related scripts finish loading
        this.enqueue(() => {
            this._tagLoadedToFirstAdMetric?.start();
            fireEREvt("ad-gpt-pubads-loaded", {});

            if (enableLazyLoad) {
                window.googletag.setConfig({
                    lazyLoad: {
                        // Fetch slots immediately (null disables the fetch margin;
                        // omitting it would fall back to Google's lazy default)
                        fetchMarginPercent: null,
                        // Render slots within 30% of viewport.
                        renderMarginPercent: 30,
                        // Double the above values on mobile, where viewports are smaller
                        // and users tend to scroll faster.
                        mobileScaling: 2.0
                    }
                });
            }
        });

        const loadMetric = this._performanceMonitor.createMetric("load");

        loadMetric.start();
        fireEREvt("ad-gpt-load-start", {});

        this.loadScriptDependency(GPT_SRC).then(() => {
            loadMetric.end();
            fireEREvt("ad-gpt-load-end", {});
        });
    }

    static html(): string {
        return `<script src="${ GPT_SRC }" async></script>`;
    }

    /**
     * Binds various callback methods to `this`
     */
    private _contextifyCallbacks() {
        this._handleSlotRenderEvent = this._handleSlotRenderEvent.bind(this);
        this._handleSlotLoadEvent = this._handleSlotLoadEvent.bind(this);
        this._handleSlotResponseReceivedEvent = this._handleSlotResponseReceivedEvent.bind(this);
        this._handleSlotRequestedEvent = this._handleSlotRequestedEvent.bind(this);
    }

    private _defineSlot(ad: Ad, services: string[] = []) {
        let slot;

        if (ad.isInterstitial()) {
            slot = this._googletag.defineOutOfPageSlot(ad.getUnitName(), ad.getId());
        } else {
            slot = this._googletag.defineSlot(ad.getUnitName(), ad.getSizes(), ad.getId());
        }

        if (!slot) {
            return null;
        }

        const targeting = ad.getTargeting();

        ad.setSlot(slot);

        slot.setConfig({ targeting: withoutEmptyValues(targeting) });
        setImpressionIdTargeting(slot);
        setRefreshTargeting(slot, ad.getRefreshCount());

        services.forEach((service) => slot.addService(this._googletag[service]()));

        this._cachedSlots[ad.getId()] = slot;

        return slot;
    }

    private _display(ad: Ad) {
        fireEREvt("ad-display", { "container-id": ad.getContainerId() });
        this._doubleVerifyTracking.dvtagReadyPromise().then(() => {
            // For shadow DOM, pass element reference directly
            // For regular DOM, pass ID string
            this._googletag.display(ad.getShadowHostElement() ? ad.getElement() : ad.getDomId());
        });
    }

    /**
     * Get ad from rendered ads store for use in rendered/empty callbacks
     */
    private _getRenderedAd(domId: string) {
        return this._renderedAds[domId] || null;
    }

    /**
     * Dispatches rendering events to the correct Ad and removes from unrendered ad registry
     */
    private _handleSlotRenderEvent(event: googletag.events.SlotRenderEndedEvent) {
        const ad = this._getRenderedAd(event.slot.getSlotElementId());

        if (ad) {
            ad.handleSlotRenderEvent(event);
        }
    }

    /**
     * Record the duration between gpt scripts finishing loading and first ad requested
     */
    private _handleSlotRequestedEvent() {
        // Mark the time when the first ad request is sent. This metric should
        // match GAM's metrics of "Tag loaded to first ad request time"
        if (this._tagLoadedToFirstAdMetric) {
            this._tagLoadedToFirstAdMetric.end();
            this._tagLoadedToFirstAdMetric = null;
            fireEREvt("ad-first-slot-requested", {});
        }
    }

    /**
     * Dispatches event when ad has fully finished loading Ad iframe
     */
    private _handleSlotLoadEvent(event: googletag.events.SlotOnloadEvent) {
        const ad = this._getRenderedAd(event.slot.getSlotElementId());

        if (ad) {
            ad.handleSlotOnLoadEvent(event);
        }
    }

    private _handleSlotResponseReceivedEvent(event: googletag.events.SlotResponseReceived) {
        const ad = this._getRenderedAd(event.slot.getSlotElementId());

        if (ad) {
            ad.handleSlotResponseReceivedEvent();
        }
    }

    /**
     * Binds listener to GPT's slot render event
     */
    private _listenToSlotRenderEvents() {
        this.enqueue(() => {
            Object.keys(SERVICES).forEach((service) => {
                const gservice = this._googletag[service]();

                gservice.addEventListener(SLOT_RENDER_EVENT, this._handleSlotRenderEvent);
                gservice.addEventListener(SLOT_ONLOAD_EVENT, this._handleSlotLoadEvent);
                // eslint-disable-next-line max-len
                gservice.addEventListener(SLOT_RESPONSE_RECEIVED_EVENT, this._handleSlotResponseReceivedEvent);
                gservice.addEventListener(SLOT_REQUESTED, this._handleSlotRequestedEvent);
            });
        });
    }

    /**
     * Refresh slots
     */
    private _refreshSlots(slots: googletag.Slot[], { changeCorrelator = false } = {}) {
        slots.forEach(setImpressionIdTargeting);
        this._doubleVerifyTracking.dvtagReadyPromise().then(() => {
            this._googletag.pubads().refresh(slots, { changeCorrelator });
        });

    }

    /**
     * Refreshes the cached slot for an ad if it exists
     * Returns true if successful, false if not
     */
    private _renderCachedSlot(ad: Ad, options = {}) {
        const slot = this._cachedSlots[ad.getId()];

        if (!slot) {
            return false;
        }

        // A new Ad picking up a cached slot is a first delivery for its page view, so it
        // clears the refresh targeting the previous ad left behind. The same Ad arriving
        // here again is re-requesting a slot it already holds, which is a refresh.
        const isRerequest = ad.getSlot() === slot;

        ad.setSlot(slot);
        setRefreshTargeting(slot, isRerequest ? ad.incrementRefreshCount() : ad.getRefreshCount());
        this._storeRenderedAd(ad);
        this._refreshSlots([slot], options);

        return true;
    }

    /**
     * Stores a reference to rendered ad for render/empty callbacks
     */
    private _storeRenderedAd(ad: Ad) {
        this._renderedAds[ad.getDomId() as string] = ad;
    }

    /**
     * Configure all ad slots on the page to be expanded by default,
     * but collapse slots that are unable to be filled with an ad.
     * https://developers.google.com/publisher-tag/reference#googletag.config.PageSettingsConfig.collapseDiv
     */
    private _collapseEmptyDivs() {
        this.enqueue(() => this._googletag.setConfig({ collapseDiv: "ON_NO_FILL" }));
    }

    /**
     * Factory method for GPT Ad
     */
    createAd(
        viewability: Viewability,
        containerId: string,
        adCode: string,
        dimensions: AdSizeMap,
        targeting: Targeting,
        isDecoupled: boolean,
        shadowHostElement?: HTMLElement
    ): Ad {
        return new Ad(viewability, containerId, `/${ this._networkCode }/${ adCode }`, dimensions, targeting, isDecoupled, shadowHostElement);
    }

    /**
     * Sets GPT personalization data based on user consent
     */
    setPersonalization(userInfo: UserInfo, gdprApplies?: boolean): Promise<string | undefined | null | void> {
        return cmpCookie.padAllowedAsync()
            .then((padAllowed) => {
                if (padAllowed) {
                    return userInfo
                        .getSessionId()
                        .then((sessionId) => {
                            const sanitizedSessionId = stripSlashes(sessionId);

                            this.enqueue(() => {
                                this._googletag.pubads().setPublisherProvidedId(sanitizedSessionId);
                            });

                            fireEREvt("ad-personalization-ret", { sessionId });

                            return sessionId;
                        })
                        .catch((e) => {
                            fireEREvt("ad-personalization-err", e);

                            return null;
                        });
                } else if (gdprApplies) {
                    // npa flag is only applicable for GDPR and us_privacy string is used for MSPS
                    this.enqueue(() => {
                        this._googletag.pubads().setPrivacySettings({ nonPersonalizedAds: true });
                    });
                }

                return null;
            })
            .catch(() => {
                this.enqueue(() => {
                    this._googletag.pubads().setPrivacySettings({ nonPersonalizedAds: true });
                });
            });
    }

    /**
     * Renders the ad
     * https://developers.google.com/doubleclick-gpt/reference#googletag.defineSlot
     * https://developers.google.com/doubleclick-gpt/reference#googletag.display
     * https://developers.google.com/doubleclick-gpt/reference#googletag.enableServices
     *
     * @param {Ad} ad
     * @param {object} config
     * @param {string[]} [config.services=[]]
     * @param {boolean} [config.display=true]
     * @returns {Promise}
     */
    render(ad: Ad, { services = [],
        display = true,
        changeCorrelator = false }: ServiceConfig): Promise<void> {
        return new Promise((resolve) => {
            this.enqueue(() => {
                ad.setRequestTime(Environment.currentTime());

                const cached = this._renderCachedSlot(ad, { changeCorrelator });

                if (cached) {
                    return resolve();
                }

                const slot = this._defineSlot(ad, services);

                if (!slot) {
                    return resolve();
                }

                this._storeRenderedAd(ad);

                this._doubleVerifyTracking.callOnDvTagReady();

                const slotDependencies = [];

                if (this._adsServiceClient) {
                    const adservicePromise =
                        this._adsServiceClient.getTargetingsForAdSync().then(
                            (targetings) => {
                                ad.getSlot()?.setConfig({ targeting: withoutEmptyValues(targetings) });
                            }
                        );

                    // for now, we don't wait for all the async targetings
                    this._adsServiceClient.getTargetingsForAdAsync().then(
                        (targetings) => {
                            ad.getSlot()?.setConfig({ targeting: withoutEmptyValues(targetings) });
                        }
                    );
                    slotDependencies.push(adservicePromise);
                }

                if (this._watchlistProvider) {
                    // do not wait for watchlist, it will be done at background.
                    this._watchlistProvider.getTargeting().then(
                        (targetings) => {
                            ad.getSlot()?.setConfig({ targeting: withoutEmptyValues(targetings) });
                        }
                    );
                }

                return Promise.allSettled(slotDependencies)
                    .then(() => {
                        fireEREvt("ad-render-start", { "container-id": ad.getContainerId() });
                        ad.dispatchEvent(Ad.EVENTS.ON_BEFORE_RENDER);

                        this._googletag.enableServices();

                        if (display) {
                            this._display(ad);
                        }
                    })
                    .catch((err) => console.warn(err))
                    .finally(resolve);
            });
        });
    }

    /**
     * Renders the ad as fast as possible (ignores bids)
     * Used for superburst parameter ad call
     */
    renderInstant(ad: Ad, { services = [] }: ServiceConfig): void {
        this.enqueue(() => {
            ad.dispatchEvent(Ad.EVENTS.ON_BEFORE_RENDER);
            ad.setRequestTime(Environment.currentTime());

            const cached = this._renderCachedSlot(ad);

            if (cached) {
                return;
            }

            const slot = this._defineSlot(ad, services);

            if (!slot) {
                return;
            }

            this._googletag.enableServices();
            this._storeRenderedAd(ad);
            this._display(ad);
        });
    }

    /**
     * Enables single request mode for fetching multiple ads at the same time.
     * https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_enableSingleRequest
     */
    enableSingleRequest(): void {
        this.enqueue(() => this._googletag.pubads().enableSingleRequest());
    }

    /**
     * Inform GPT that video ads will be present on the page
     * https://developers.google.com/publisher-tag/reference#googletag.config.PageSettingsConfig.videoAds
     */
    enableVideoAds(): void {
        this.enqueue(() => this._googletag.setConfig({ videoAds: { enableVideoAds: true } }));
    }

    /**
     * Push callback to GPT
     */
    enqueue(cb: () => void): void {
        if (this._googletag) this._googletag.cmd.push(cb);
    }

    /**
     * Refresh a list of ads
     * https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_refresh
     */
    refresh(ads: Ad[], options = {}): void {
        ads
            .filter((ad) => ad.hasSlot())
            .forEach((ad) => {
                const slot = ad.getSlot() as googletag.Slot;

                ad.dispatchEvent(Ad.EVENTS.ON_BEFORE_RENDER);

                this.enqueue(() => {
                    setRefreshTargeting(slot, ad.incrementRefreshCount());
                    this._refreshSlots([slot], options);
                });
            });
    }

    /**
     * Enable/disable companion ad backfilling of unfilled slots
     */
    setRefreshUnfilledSlots(value: boolean): void {
        this.enqueue(() => this._googletag.companionAds().setRefreshUnfilledSlots(value));
    }

    /**
     * Page-level targeting. setConfig merges targeting per key, so other keys are kept.
     * https://developers.google.com/publisher-tag/reference#googletag.config.PageSettingsConfig.targeting
     */
    setTargeting(name: string, value: string | string[]): void {
        this.enqueue(() => this._googletag.setConfig({ targeting: { [name]: value } }));
    }

    /**
     * Return the list of all GPT page-level targeting keys.
     */
    getTargetingKeys(): string[] {
        return Object.keys(this._getPageTargeting());
    }

    /**
     * Return GPT page-level targeting info for a specific key.
     */
    getTargeting(key: string): string | string[] {
        return this._getPageTargeting()[key] ?? [];
    }

    private _getPageTargeting() {
        return this._googletag.getConfig("targeting").targeting ?? {};
    }

    /**
     * Update correlator value
     * https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_updateCorrelator
     */
    updateCorrelator(): void {
        this.enqueue(() => this._googletag.pubads().updateCorrelator());
    }

    getEnableLazyLoad(): boolean {
        return this._enableLazyLoad;
    }

    /**
     * Cleans up references to ads
     */
    destroy() {
        const slots = Object.keys(this._cachedSlots).map((key) => this._cachedSlots[key]);

        this._googletag.cmd.push(() => this._googletag.destroySlots(slots));
        this._cachedSlots = {};
        this._googletag = null;
        this._renderedAds = {};

        super.destroy();
    }

}

/**
 * setConfig clears a targeting key whose value is null, so drop empty values
 * instead of letting them erase targeting already on the slot.
 */
function withoutEmptyValues(targeting: Targeting): Targeting {
    const result: Targeting = {};

    Object.keys(targeting).forEach((key) => {
        if (targeting[key] !== undefined && targeting[key] !== null) {
            result[key] = targeting[key];
        }
    });

    return result;
}

function setImpressionIdTargeting(slot: googletag.Slot) {
    slot.setConfig({ targeting: { iid: uuidv4() } });
}

/**
 * Marks how deep into the refresh cycle this ad request is, so GAM reporting
 * can compare a first delivery (`refresh=0`) against a refreshed one
 * (`refresh=1`, `refresh=2`, ...).
 *
 * Depth is bucketed past {@link MAX_REPORTED_REFRESH} to keep the number of
 * `refresh` values bounded — a long session can refresh a slot on every
 * viewport re-entry, and GAM needs each value predefined to report on it. The
 * exact count still reaches the `ad-render` event recorder payload, so
 * reporting off `ad_impressions` is unaffected.
 */
function setRefreshTargeting(slot: googletag.Slot, refreshCount: number) {
    const depth = refreshCount < MAX_REPORTED_REFRESH
        ? String(refreshCount)
        : `${ MAX_REPORTED_REFRESH }plus`;

    slot.setConfig({ targeting: { refresh: depth } });
}
